import theme from '@/assets/theme';
import CustomShipmentText from '@/components/CustomShipmentText';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { CoCEventType } from '@/common/enum';
import { Col, Divider, Flex, Row } from 'antd';
import React, { memo } from 'react';
import { ShipmentVitals_CT } from '../partials';
import DeliveryStatusTag from './DeliveryStatusTag';
import ExternalMapCOC from './ExternalMapCOC';
import ShareAlertsCarousel from './ShareAlertsCarousel';
import SharedMapCOC from './SharedMapCOC';
import './Style.css';
import { SharePageContentProps } from './types';

// ---- Small typography helpers ----

const LabelText: React.FC<{ text: string; marginBottom?: string }> = ({
  text,
  marginBottom = '4px',
}) => (
  <>
    <CustomShipmentText
      text={text}
      fontWeight={600}
      marginBottom={marginBottom}
    />
  </>
);

const ValueText: React.FC<{
  text: string;
  marginBottom?: string;
}> = ({ text, marginBottom = '0' }) => (
  <>
    <CustomShipmentText
      text={text}
      color='var(--text-light)'
      marginBottom={marginBottom}
    />
  </>
);

const TruncatedValueText: React.FC<{
  text?: string;
  max?: number;
  marginBottom?: string;
}> = ({ text = '', max = 23, marginBottom = '0' }) => (
  <TruncateWithTooltip
    text={text}
    max={max}
    render={(t) => <ValueText text={t} marginBottom={marginBottom} />}
  />
);

type InfoColProps = {
  label: string;
  value?: string;
  title?: string;
  truncate?: boolean;
  max?: number;
  children?: React.ReactNode;
};

const SharedColProps = {
  xxl: 12,
  xl: 12,
  lg: 12,
  md: 12,
  sm: 12,
  xs: 12,
} as const;

const InfoCol: React.FC<InfoColProps> = ({
  label,
  value = '',
  title,
  truncate = false,
  max = 23,
  children,
}) => (
  <Col title={title ?? value} {...SharedColProps}>
    <LabelText text={label} />
    {children ? (
      children
    ) : truncate ? (
      <TruncatedValueText text={value} max={max} />
    ) : (
      <ValueText text={value} />
    )}
  </Col>
);

const PageContent: React.FC<SharePageContentProps> = memo((props) => {
  const {
    isSharePage,
    variant,
    showAlerts,
    alertsData,
    alertIndex,
    setAlertIndex,
    shipment,
    shipmentId,
    productDetails,
    capitalize,
    getFormattedTime,
    downloadFile,
    halfHeight,
    setHalfHeight,
    setHalfHeightDirect,
    showFull,
    setShowFull,
    arrowLink,
    fetchSingleShipmentData,
    uuid,
  } = props;

  const isMobile = variant === 'mobile';
  const trackingMax = isMobile ? 13 : 18;

  const isDeliveredType = String(shipment?.type) === '3';
  const etaOrDeliveredLabel = isDeliveredType ? 'Delivered on' : 'Zoomlogi ETA';
  const etaOrDeliveredValue = isDeliveredType
    ? shipment?.actual_delivery_date
    : shipment?.estimated_delivery_date;

  const product = shipment?.product;
  const sensor = shipment?.sensor;

  const productFields = [
    {
      key: 'name',
      label: 'Product',
      value: capitalize?.(String(product?.name ?? '')) ?? '',
      truncate: true,
    },
    {
      key: 'quantity',
      label: 'Product quantity',
      value: String(product?.quantity ?? ''),
      truncate: true,
    },
    {
      key: 'product_type',
      label: 'Product type',
      value: String(product?.product_type ?? ''),
      truncate: true,
    },
    {
      key: 'project_id',
      label: 'Project ID',
      value: String(product?.project_id ?? ''),
      truncate: true,
    },
    {
      key: 'temperature_range',
      label: 'Temperature range',
      value: String(product?.temperature_range ?? ''),
      truncate: true,
    },
    {
      key: 'viability_window',
      label: 'Transit viability window',
      value: `${String(product?.viability_window ?? '')} hrs`,
      truncate: true,
    },
  ];

  const filteredChainOfCustody =
    shipment?.chain_of_custody?.filter(
      (custody) =>
        custody?.type !== CoCEventType.Comment ||
        (custody?.type === CoCEventType.Comment && showAlerts)
    ) ?? [];

  return (
    <>
      {(!isSharePage ||
        (isSharePage && showAlerts && (alertsData?.length ?? 0) > 0)) && (
        <ShareAlertsCarousel
          alerts={alertsData!}
          index={alertIndex ?? 0}
          setIndex={setAlertIndex ?? (() => undefined)}
          isSharePage={isSharePage}
          shipmentId={shipmentId}
        />
      )}

      <Row gutter={[16, 16]} justify='space-between'>
        <InfoCol label='Delivery status'>
          <DeliveryStatusTag status={shipment?.delivery_status} />
        </InfoCol>

        <InfoCol
          label={etaOrDeliveredLabel}
          value={String(etaOrDeliveredValue ?? '')}
          title={String(etaOrDeliveredValue ?? '')}
          truncate
          max={23}
        />

        <InfoCol
          label='Destination'
          value={String(shipment?.to_location?.address ?? '')}
          title={String(shipment?.to_location?.address ?? '')}
        />

        <InfoCol
          label='Scheduled delivery'
          value={String(shipment?.scheduled_delivery_date ?? '')}
          title={String(shipment?.scheduled_delivery_date ?? '')}
        />

        <InfoCol
          label={theme.themeType === 2 ? 'Carrier' : 'Transport provider'}
          value={String(shipment?.courier?.title ?? '')}
          title={String(shipment?.courier?.title ?? '')}
          truncate
          max={23}
        />

        {/* Tracking ID (special case with link); a masked share link hides it */}
        {!shipment?.share_settings?.mask_tracking_link && (
          <Col {...SharedColProps}>
            <CustomShipmentText
              text='Tracking ID'
              fontWeight={600}
              marginBottom='4px'
            />
            {shipment?.courier?.tracking_link ? (
              <a
                href={shipment.courier.tracking_link}
                target='_blank'
                rel='noopener noreferrer'
                className='c-pointer'
              >
                <Flex>
                  <TruncatedValueText
                    text={String(shipment?.courier_tracking_id ?? '')}
                    max={trackingMax}
                  />
                  <img
                    src={arrowLink}
                    alt='arrow'
                    width={15}
                    className='ml-3'
                  />
                </Flex>
              </a>
            ) : (
              <span className='c-pointer fit-content'>
                <TruncatedValueText
                  text={String(shipment?.courier_tracking_id ?? '')}
                  max={isMobile ? 16 : 23}
                />
              </span>
            )}
          </Col>
        )}
      </Row>

      {/* Vitals */}
      {shipment?.can_view_vitals &&
        (shipment?.sensor || (shipment?.sensor_summary?.length ?? 0) > 0) && (
          <Flex className={isMobile ? 'vitals--mobile' : 'vitals--desktop'}>
            <ShipmentVitals_CT
              shipment={shipment}
              openCharts
              shareToken={uuid}
            />
          </Flex>
        )}

      <Divider className='no-margin' />

      {/* Product details */}
      {(!isSharePage || productDetails) && (
        <>
          <Flex justify='space-between' align='center' gap={16}>
            <CustomShipmentText
              text='Product details'
              fontSize='14px'
              fontWeight={600}
              lineHeight='28px'
            />
          </Flex>

          <Row gutter={[16, 16]} justify='space-between'>
            {productFields.map(({ key, label, value, truncate }) => (
              <InfoCol
                key={key}
                label={label}
                value={value}
                truncate={truncate}
                max={23}
              />
            ))}

            {/* Sensor */}
            <InfoCol
              label='Sensor'
              value={sensor?.type ? String(sensor.type) : 'N/A'}
              truncate={!!sensor?.type}
              max={23}
              title={String(sensor?.type ?? 'N/A')}
            />

            {/* Sensor ID */}
            <InfoCol
              label='Sensor ID'
              value={String(sensor?.id ?? '')}
              truncate
              max={23}
              title={String(sensor?.id ?? '')}
            />
          </Row>
        </>
      )}

      {isSharePage ? (
        <SharedMapCOC
          shipment={shipment}
          shipmentId={shipmentId}
          halfHeight={halfHeight}
          setHalfHeight={setHalfHeight}
          setHalfHeightDirect={setHalfHeightDirect}
          showFull={showFull}
          setShowFull={setShowFull}
          downloadFile={downloadFile}
          getFormattedTime={getFormattedTime}
          fetchSingleShipmentData={fetchSingleShipmentData}
          filteredChainOfCustody={filteredChainOfCustody}
        />
      ) : (
        <ExternalMapCOC
          shipment={shipment}
          shipmentId={shipmentId}
          halfHeight={halfHeight}
          setHalfHeight={setHalfHeight}
          setHalfHeightDirect={setHalfHeightDirect}
          showFull={showFull}
          setShowFull={setShowFull}
          downloadFile={downloadFile}
          getFormattedTime={getFormattedTime}
          fetchSingleShipmentData={fetchSingleShipmentData}
        />
      )}
    </>
  );
});

PageContent.displayName = 'SharePageContent';
export default PageContent;
